/**
 * Why a registrar-signature request ("Ký SmartCA", "Gửi ký ngay") was refused, as the codes
 * admin-api answers (subsystems/identity/facade/adapters/admin-api/src/admin/post_operations.rs) — the page shows them in
 * Vietnamese, never the server's raw text; the code and request id stay behind "Chi tiết".
 *
 * Pure functions (no React): `npm test` covers them.
 */

export type SigningErrorCode = 'PRECHECK_FAILED' | 'SIGNING_OFF' | 'SMARTCA_NOT_CONFIGURED' | 'SIGNING_NOT_READY' | 'NOTHING_TO_SIGN' | 'INVALID' | 'OTHER';

export interface SigningErrorView {
  code: SigningErrorCode;
  /** PRECHECK_FAILED: the failing checks, Vietnamese sentences from the core. */
  reasons: string[];
  /** SIGNING_NOT_READY: files waiting for the checks / for staff. */
  pendingCheck: number;
  review: number;
  /** Show the way to "Cài đặt › Kết nối › SmartCA". */
  settingsLink: boolean;
  /** INVALID: the server's reason (Vietnamese: `registrar_signature::request_file`). */
  serverMessage: string | null;
}

const KNOWN = new Set<SigningErrorCode>(['PRECHECK_FAILED', 'SIGNING_OFF', 'SMARTCA_NOT_CONFIGURED', 'SIGNING_NOT_READY', 'NOTHING_TO_SIGN', 'INVALID']);

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/** The view of an API error (`{ code, message, details }`); anything else is `OTHER`. */
export function signingError(error: { code?: unknown; message?: unknown; details?: unknown } | null | undefined): SigningErrorView {
  const raw = typeof error?.code === 'string' ? error.code : '';
  const code: SigningErrorCode = KNOWN.has(raw as SigningErrorCode) ? (raw as SigningErrorCode) : 'OTHER';
  const details = (error?.details && typeof error.details === 'object' ? error.details : {}) as Record<string, unknown>;
  const reasons = Array.isArray(details.reasons) ? details.reasons.filter((r): r is string => typeof r === 'string' && r.trim() !== '') : [];
  return {
    code,
    reasons: code === 'PRECHECK_FAILED' ? reasons : [],
    pendingCheck: code === 'SIGNING_NOT_READY' ? num(details.pending_check) : 0,
    review: code === 'SIGNING_NOT_READY' ? num(details.review) : 0,
    settingsLink: code === 'SIGNING_OFF' || code === 'SMARTCA_NOT_CONFIGURED',
    serverMessage: code === 'INVALID' && typeof error?.message === 'string' ? error.message : null,
  };
}
